/**
 * What a stale keybook URL shows when the deployment has the keybook switched
 * off (0179).
 *
 * Same rule as `IdentityOnlyNotice` next door: *a URL a viewer cannot use is
 * refused in words, not redirected* — a bookmark or the Back button should be
 * told what happened rather than silently teleported home. Shared by both
 * shells for the same reason that component is: desktop `App.tsx` and
 * `MobileShell` mount separate trees, and copy written twice drifts.
 *
 * Unlike identity-only, "disabled" is the honest word here — the operator
 * turned the feature off deployment-wide, and nothing about the viewer's own
 * account explains it.
 */
import { useTranslation } from '@papan/i18n'

export function KeybookDisabledNotice({ onGoHome }: { onGoHome?: () => void }) {
  const { t } = useTranslation('core-ui')

  return (
    <div
      className="flex-1 flex items-center justify-center p-8"
      data-testid="keybook-disabled-notice"
    >
      <div className="max-w-md text-center">
        <div className="mx-auto mb-4 h-12 w-12 rounded-2xl themed-bg-tertiary flex items-center justify-center">
          {/* Inline for the same reason IdentityOnlyNotice's is: no icon
              library for one glyph. A key with a soft slash. */}
          <svg
            viewBox="0 0 24 24" width={24} height={24} aria-hidden
            fill="none" stroke="currentColor" strokeWidth={1.5}
            strokeLinecap="round" strokeLinejoin="round"
            className="themed-text-muted"
          >
            <circle cx="8" cy="15" r="4" />
            <path d="m10.85 12.15 8.5-8.5M17 5l2 2M14.5 7.5l2 2" />
            <path d="M4 20 20 4" className="opacity-60" />
          </svg>
        </div>

        <h2 className="themed-text text-base font-medium mb-2">
          {t('keybookDisabled.title', { defaultValue: 'Keybook is not available' })}
        </h2>

        <p className="themed-text-secondary text-sm leading-relaxed">
          {t('keybookDisabled.body', {
            defaultValue:
              'Credential storage is switched off on this deployment, so there is no keybook page here. Everything else — your files, organizations and apps — works as usual.',
          })}
        </p>

        {onGoHome && (
          <button
            onClick={onGoHome}
            className="mt-4 px-4 py-2 text-sm rounded-lg themed-btn-secondary"
          >
            {t('keybookDisabled.goHome', { defaultValue: 'Go home' })}
          </button>
        )}
      </div>
    </div>
  )
}
